// onboarding.ts — the shared onboarding overlay engine + step sequencer.
//
// One fixed scrim dims + blurs the whole viewport; the spotlit controls are
// TRUE CUTOUTS punched with `clip-path: path(evenodd, …)` — not box-shadow,
// not four rects, not a mask. Because the holes are real cutouts in the
// overlay element:
//   1. the spotlit elements show through CRISP (the blurring element simply
//      is not there over the hole);
//   2. pointer events pass through the holes to the real controls — the
//      spotlit button stays natively clickable, no event forwarding, no
//      cloned ghost element;
//   3. it sidesteps every stacking-context fight: the engine never raises a
//      target, it just does not cover it. Scrim z-index 1200 / content layer
//      1201 sit above both consumers' ladders (measured max 1000).
//
// On top of the engine sits a step SEQUENCER: a flow is an ordered list of
// steps (targets, title, body, placement) with Back/Next, progress dots and
// a "Skip intro" affordance on every multi-step flow that ends the WHOLE
// flow. Deliberate interaction semantics (each of these is a decision, see
// docs/onboarding-overlays.md):
//   - Next / clicking the scrim / clicking THROUGH a hole → advance one step
//     (a stray click never kills the tour; the spotlit control also fires
//     natively on click-through).
//   - "Skip intro" → end the whole flow (reason 'skipped').
//   - Escape → end the whole flow (reason 'dismissed').
//   - Back at step 0 is a no-op; Back never wraps.
//   - A step whose targets are ALL absent after a double-rAF probe is
//     auto-skipped and VISIBLY LOGGED — never a stuck holeless scrim (the
//     reference implementation's known defect; deliberately fixed here).
//   - A step with `targets: []` is an INTENTIONAL modal step (full scrim,
//     centered card) — e.g. a welcome step. Only non-empty target lists
//     participate in auto-skip.
//   - Exactly one flow at a time; a second startFlow() while one is active
//     refuses (first committed wins) and logs.
//
// Measurement discipline (ported from the reference, hardened):
//   - all coordinates are viewport coords from getBoundingClientRect() —
//     exactly what clip-path on a position:fixed inset:0 element wants;
//   - re-measure on: double-rAF after each render (late mounts/animations),
//     window resize, CAPTURE-phase scroll (nested scrollers re-clip the
//     holes), a ResizeObserver per target, and a rAF-coalesced
//     MutationObserver (a target re-mounting mid-step re-resolves BY ID —
//     the engine holds ids, never stale element refs);
//   - an off-viewport target is scrollIntoView'd (instant, block:'nearest')
//     before measuring, so a hole never clips to an off-screen rect.
//
// The engine is persistence-free: flow lifecycle is reported through
// OnboardingHooks.onFlowEnd and the seen-state store lives with the flow
// registry (PR 2). Copy is applied via textContent ONLY — never innerHTML —
// because the programmatic seam (PR 3) accepts runtime-supplied text.
//
// Styling: the classes below (`ob-*`) are defined in EACH consumer's own
// styles.css in that consumer's theme tokens (the Bridge's --surface/--ink
// family, the console's --hc-* family) — same shared-layer split as
// tooltip.ts / `.bridge-tip`. Both twins carry a prefers-reduced-motion
// block collapsing the entrance animations.

import { mkIconBtn } from './buttons.js';

export type SpotShape = 'rect' | 'circle';

export interface SpotTarget {
  /** DOM id of the element to spotlight (no leading '#'). */
  id: string;
  /** Cutout shape; 'rect' (default) draws a rounded rect. */
  shape?: SpotShape;
  /** Inflation around the element's rect, px (default 8). */
  padding?: number;
}

export type CardPlacement = 'auto' | 'below' | 'above' | 'left' | 'right' | 'center';

export interface FlowStep {
  /** Elements to spotlight. Empty = intentional modal step (full scrim). */
  targets: SpotTarget[];
  title: string;
  body: string;
  /** Card placement relative to the union of the holes (default 'auto'). */
  placement?: CardPlacement;
}

export interface FlowDef {
  /** Stable flow id — the persistence key (PR 2) and startFlow(id) key (PR 3). */
  id: string;
  /** Surface key (route) the flow belongs to; used by first-visit triggers. */
  surface: string;
  steps: FlowStep[];
}

export type FlowEndReason = 'completed' | 'skipped' | 'dismissed' | 'no-targets';

export interface FlowEndInfo {
  shownSteps: number;
  missingSteps: number;
  totalSteps: number;
}

export interface OnboardingHooks {
  onFlowEnd?: (flowId: string, reason: FlowEndReason, info: FlowEndInfo) => void;
  onStepShow?: (flowId: string, stepIndex: number) => void;
  /** Defaults to console.info. Every auto-skipped step logs through this. */
  log?: (line: string) => void;
}

export interface FlowHandle {
  readonly flowId: string;
  next(): void;
  back(): void;
  dismiss(reason?: FlowEndReason): void;
  isActive(): boolean;
}

// --------------------------------------------------------------------------
// Pure geometry — exported for headless tests (smoke transpiles this module).
// --------------------------------------------------------------------------

export interface RectLike { left: number; top: number; width: number; height: number }
export interface Viewport { w: number; h: number }

const DEFAULT_PAD = 8;
const MAX_RADIUS = 10;

/** Format a coordinate: at most 2 decimals, no trailing zeros. */
const fx = (n: number): string => String(Math.round(n * 100) / 100);

/** The padded bounding box a target's hole occupies (used for placement). */
export function paddedBox(t: SpotTarget, r: RectLike): RectLike {
  const pad = t.padding ?? DEFAULT_PAD;
  if (t.shape === 'circle') {
    const rad = Math.max(r.width, r.height) / 2 + pad;
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    return { left: cx - rad, top: cy - rad, width: rad * 2, height: rad * 2 };
  }
  return { left: r.left - pad, top: r.top - pad, width: r.width + pad * 2, height: r.height + pad * 2 };
}

/**
 * One closed sub-path cutting a hole for a target. Rects are rounded rects
 * (radius min(10, w/2, h/2)) drawn with h/v/a commands; circles are two 180°
 * arcs. Winding direction is irrelevant under evenodd.
 */
export function holePath(t: SpotTarget, r: RectLike): string {
  const box = paddedBox(t, r);
  if (t.shape === 'circle') {
    const rad = box.width / 2;
    const cy = box.top + rad;
    return (
      `M ${fx(box.left)} ${fx(cy)} ` +
      `a ${fx(rad)} ${fx(rad)} 0 1 0 ${fx(rad * 2)} 0 ` +
      `a ${fx(rad)} ${fx(rad)} 0 1 0 ${fx(-rad * 2)} 0 Z`
    );
  }
  const { left: x, top: y, width: w, height: h } = box;
  const rad = Math.min(MAX_RADIUS, w / 2, h / 2);
  return (
    `M ${fx(x + rad)} ${fx(y)} ` +
    `h ${fx(w - rad * 2)} ` +
    `a ${fx(rad)} ${fx(rad)} 0 0 1 ${fx(rad)} ${fx(rad)} ` +
    `v ${fx(h - rad * 2)} ` +
    `a ${fx(rad)} ${fx(rad)} 0 0 1 ${fx(-rad)} ${fx(rad)} ` +
    `h ${fx(-(w - rad * 2))} ` +
    `a ${fx(rad)} ${fx(rad)} 0 0 1 ${fx(-rad)} ${fx(-rad)} ` +
    `v ${fx(-(h - rad * 2))} ` +
    `a ${fx(rad)} ${fx(rad)} 0 0 1 ${fx(rad)} ${fx(-rad)} Z`
  );
}

export interface ClipResult {
  /** The full CSS clip-path value: path(evenodd, "M 0 0 H w V h H 0 Z <holes>"). */
  clip: string;
  /** How many holes were actually punched (targets measured). */
  holeCount: number;
  /** The padded boxes of the punched holes, for card placement. */
  boxes: RectLike[];
}

export function clipPathFor(
  targets: SpotTarget[],
  rects: Map<string, RectLike>,
  vp: Viewport,
): ClipResult {
  const holes: string[] = [];
  const boxes: RectLike[] = [];
  for (const t of targets) {
    const r = rects.get(t.id);
    if (!r) continue;
    holes.push(holePath(t, r));
    boxes.push(paddedBox(t, r));
  }
  const joined = holes.length ? ` ${holes.join(' ')}` : '';
  return {
    clip: `path(evenodd, "M 0 0 H ${fx(vp.w)} V ${fx(vp.h)} H 0 Z${joined}")`,
    holeCount: holes.length,
    boxes,
  };
}

export interface PlacedCard {
  left: number;
  top: number;
  mode: 'below' | 'above' | 'right' | 'left' | 'center';
}

/**
 * Place the card relative to the union box of the holes: prefer below, flip
 * above, fall back to the sides, clamp to the viewport margins. 'center' (or
 * no holes at all) centers the card — the intentional-modal-step case.
 * Placement is COMPUTED from measured rects, never a fixed offset, so cards
 * land correctly at every viewport size.
 */
export function placeCard(
  boxes: RectLike[],
  vp: Viewport,
  card: { w: number; h: number },
  placement: CardPlacement = 'auto',
  gap = 12,
  margin = 16,
): PlacedCard {
  const clampX = (x: number): number => Math.max(margin, Math.min(x, vp.w - card.w - margin));
  const clampY = (y: number): number => Math.max(margin, Math.min(y, vp.h - card.h - margin));
  const first = boxes[0];
  if (placement === 'center' || boxes.length === 0 || first === undefined) {
    return {
      left: clampX((vp.w - card.w) / 2),
      top: clampY((vp.h - card.h) / 2),
      mode: 'center',
    };
  }
  let box = { ...first };
  for (const b of boxes) {
    const right = Math.max(box.left + box.width, b.left + b.width);
    const bottom = Math.max(box.top + box.height, b.top + b.height);
    box.left = Math.min(box.left, b.left);
    box.top = Math.min(box.top, b.top);
    box = { left: box.left, top: box.top, width: right - box.left, height: bottom - box.top };
  }
  const cx = box.left + box.width / 2;
  const cy = box.top + box.height / 2;
  const below: PlacedCard = { left: clampX(cx - card.w / 2), top: box.top + box.height + gap, mode: 'below' };
  const above: PlacedCard = { left: clampX(cx - card.w / 2), top: box.top - gap - card.h, mode: 'above' };
  const right: PlacedCard = { left: box.left + box.width + gap, top: clampY(cy - card.h / 2), mode: 'right' };
  const left: PlacedCard = { left: box.left - gap - card.w, top: clampY(cy - card.h / 2), mode: 'left' };
  const fits = (p: PlacedCard): boolean =>
    p.left >= margin && p.left + card.w <= vp.w - margin &&
    p.top >= margin && p.top + card.h <= vp.h - margin;
  if (placement === 'below') return { ...below, top: clampY(below.top) };
  if (placement === 'above') return { ...above, top: clampY(above.top) };
  if (placement === 'right') return { ...right, left: clampX(right.left) };
  if (placement === 'left') return { ...left, left: clampX(left.left) };
  for (const p of [below, above, right, left]) if (fits(p)) return p;
  return { left: clampX((vp.w - card.w) / 2), top: clampY((vp.h - card.h) / 2), mode: 'center' };
}

// --------------------------------------------------------------------------
// Pure sequencer core — exported for headless tests.
// --------------------------------------------------------------------------

export type SeqEvent = 'shown' | 'next' | 'back' | 'skip' | 'dismiss' | 'missing';

export interface SeqState {
  /** Current step index; meaningless once ended. */
  index: number;
  /** Direction of travel — 'missing' auto-skips continue this way. */
  dir: 1 | -1;
  /** Step indexes actually rendered. */
  shown: number[];
  /** Step indexes auto-skipped because their targets were absent. */
  missing: number[];
  ended: boolean;
  reason: FlowEndReason | null;
}

export function seqInit(): SeqState {
  return { index: 0, dir: 1, shown: [], missing: [], ended: false, reason: null };
}

/**
 * The sequencer state machine. Ended states are absorbing. Walking off the
 * end (via 'next' or a 'missing' chain) completes the flow — reason
 * 'completed' if at least one step was shown, 'no-targets' if none ever were
 * (every step's targets absent: the flow never rendered, and the caller
 * should NOT persist it as seen).
 */
export function seqReduce(s: SeqState, ev: SeqEvent, stepCount: number): SeqState {
  if (s.ended) return s;
  const offEnd = (st: SeqState): SeqState => ({
    ...st,
    ended: true,
    reason: st.shown.length > 0 ? 'completed' : 'no-targets',
  });
  switch (ev) {
    case 'shown':
      return s.shown.includes(s.index) ? s : { ...s, shown: [...s.shown, s.index] };
    case 'next': {
      const st: SeqState = { ...s, index: s.index + 1, dir: 1 };
      return st.index >= stepCount ? offEnd(st) : st;
    }
    case 'back':
      return s.index <= 0 ? s : { ...s, index: s.index - 1, dir: -1 };
    case 'skip':
      return { ...s, ended: true, reason: 'skipped' };
    case 'dismiss':
      return { ...s, ended: true, reason: 'dismissed' };
    case 'missing': {
      const missing = s.missing.includes(s.index) ? s.missing : [...s.missing, s.index];
      let dir = s.dir;
      let index = s.index + dir;
      if (index < 0) {
        // Bounced off the front while walking back — resume forward.
        dir = 1;
        index = s.index + 1;
      }
      const st: SeqState = { ...s, missing, index, dir };
      return st.index >= stepCount ? offEnd(st) : st;
    }
  }
}

// --------------------------------------------------------------------------
// DOM controller.
// --------------------------------------------------------------------------

interface ActiveFlow {
  flowId: string;
  handle: FlowHandle;
}

let ACTIVE: ActiveFlow | null = null;

/** The flow currently on screen, if any. */
export function activeFlowId(): string | null {
  return ACTIVE ? ACTIVE.flowId : null;
}

/** Dismiss the active flow (default reason 'dismissed'); no-op when idle. */
export function dismissActive(reason: FlowEndReason = 'dismissed'): void {
  ACTIVE?.handle.dismiss(reason);
}

/**
 * Start a flow. Returns its handle, or null when refused (another flow is
 * active, the flow has no steps, or there is no DOM). Exactly one flow runs
 * at a time — first committed wins, same invariant as the reference.
 */
export function startFlow(flow: FlowDef, hooks: OnboardingHooks = {}): FlowHandle | null {
  const log = hooks.log ?? ((line: string) => console.info(line));
  if (typeof document === 'undefined') return null;
  if (flow.steps.length === 0) {
    log(`[onboarding] ${flow.id}: refused — flow has no steps`);
    return null;
  }
  if (ACTIVE) {
    log(`[onboarding] ${flow.id}: refused — ${ACTIVE.flowId} is already active (one flow at a time)`);
    return null;
  }

  const steps = flow.steps;
  const stepCount = steps.length;
  let state = seqInit();
  let scrim: HTMLDivElement | null = null;
  let layer: HTMLDivElement | null = null;
  let card: HTMLDivElement | null = null;
  let ro: ResizeObserver | null = null;
  let mo: MutationObserver | null = null;
  let moScheduled = false;
  let renderToken = 0; // invalidates in-flight rAF probes when the step moves on
  // The composed entrance (owner ruling, item 63 follow-up): the scrim
  // establishes (fade + blur-in, CSS ob-enter) and the FIRST card follows a
  // beat later (.ob-entrance animation-delay). Later steps swap cards with
  // the plain card entrance — the scrim node persists, so its entrance
  // naturally plays once per flow.
  let enteredOnce = false;
  const priorFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;

  const stepLabel = (i: number): string => {
    const st = steps[i];
    if (!st) return `step ${i + 1}/${stepCount}`;
    const ids = st.targets.map((t) => t.id).join(', ');
    return `step ${i + 1}/${stepCount} (${ids || 'modal'})`;
  };

  const measureTargets = (step: FlowStep): { rects: Map<string, RectLike>; els: HTMLElement[] } => {
    const rects = new Map<string, RectLike>();
    const els: HTMLElement[] = [];
    for (const t of step.targets) {
      const el = document.getElementById(t.id);
      if (!el || !el.isConnected) continue;
      const r = el.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) continue;
      rects.set(t.id, { left: r.left, top: r.top, width: r.width, height: r.height });
      els.push(el);
    }
    return { rects, els };
  };

  const viewport = (): Viewport => ({ w: window.innerWidth, h: window.innerHeight });

  const targetSelector = (step: FlowStep): string =>
    step.targets.map((t) => `#${CSS.escape(t.id)}`).join(', ');

  const ensureNodes = (): void => {
    if (!scrim) {
      scrim = document.createElement('div');
      scrim.className = 'ob-scrim';
      scrim.setAttribute('data-onboarding', '');
      scrim.setAttribute('aria-hidden', 'true');
      scrim.addEventListener('pointerdown', (e) => {
        // A stray click never kills the tour — it advances one step.
        e.preventDefault();
        advance();
      });
      document.body.appendChild(scrim);
    }
    if (!layer) {
      layer = document.createElement('div');
      layer.className = 'ob-layer';
      layer.setAttribute('data-onboarding', '');
      document.body.appendChild(layer);
    }
  };

  const buildCard = (step: FlowStep, index: number): HTMLDivElement => {
    const el = document.createElement('div');
    el.className = 'ob-card';
    el.setAttribute('data-onboarding', '');
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-label', `${step.title} (step ${index + 1} of ${stepCount})`);

    const title = document.createElement('div');
    title.className = 'ob-title';
    title.textContent = step.title;
    el.appendChild(title);

    const body = document.createElement('div');
    body.className = 'ob-body';
    body.textContent = step.body;
    el.appendChild(body);

    const foot = document.createElement('div');
    foot.className = 'ob-foot';

    const dots = document.createElement('div');
    dots.className = 'ob-dots';
    dots.setAttribute('aria-hidden', 'true');
    if (stepCount > 1) {
      for (let i = 0; i < stepCount; i++) {
        const d = document.createElement('span');
        d.className = i === index ? 'ob-dot on' : 'ob-dot';
        dots.appendChild(d);
      }
    }
    foot.appendChild(dots);

    const nav = document.createElement('div');
    nav.className = 'ob-nav';
    if (stepCount > 1) {
      const skip = mkIconBtn({ glyph: 'x', label: 'Skip intro', size: 'sm', cls: 'ob-skip' });
      skip.addEventListener('click', () => finish('skipped'));
      nav.appendChild(skip);
    }
    if (index > 0) {
      const backBtn = mkIconBtn({ glyph: 'arrow-left', label: 'Back', cls: 'ob-btn ob-back' });
      backBtn.addEventListener('click', () => goBack());
      nav.appendChild(backBtn);
    }
    // Two call sites, not one with a ternary intent: the LAST step's button
    // is the one additive action of the tour (it finishes it), every earlier
    // one is plain navigation — and the gate reads literal intents.
    const primary =
      index === stepCount - 1
        ? mkIconBtn({
            glyph: 'check',
            label: 'Done',
            intent: 'additive',
            ctx: 'pi-onboarding',
            cls: 'ob-btn ob-primary',
          })
        : mkIconBtn({ glyph: 'arrow-right', label: 'Next', cls: 'ob-btn ob-primary' });
    primary.addEventListener('click', () => advance());
    nav.appendChild(primary);
    foot.appendChild(nav);
    el.appendChild(foot);
    return el;
  };

  /** Re-measure the CURRENT step; re-apply clip + card position ONLY when
   * the placement inputs actually changed.
   *
   * Owner defect (2026-09-05, measured -3.00px by the shift-probes gate):
   * the card shifted a few px up-left ~1s after placing. Two rules fixed it,
   * both load-bearing:
   *   - SIZE FROM LAYOUT, NEVER FROM THE TRANSFORMED RECT: the entrance
   *     animation's from-state is scale(.98), so getBoundingClientRect()
   *     mid-entrance read ~333px for a 340px card; the first churn-driven
   *     re-measure after the animation used the true size and moved the
   *     card by the half-delta. offsetWidth/offsetHeight are the
   *     untransformed layout size — the SAME number at every point of the
   *     entrance, one geometry path.
   *   - WRITE ONLY ON CHANGE: identical style writes still fire attribute
   *     mutations, and the engine's own observer would wake itself per
   *     write. A no-input settle now writes nothing (the probes count the
   *     writes); a real input change (target moved, viewport resized)
   *     recomputes and repositions. */
  let lastClip = '';
  let lastLeft = NaN;
  let lastTop = NaN;
  const measure = (): void => {
    if (state.ended) return;
    const step = steps[state.index];
    if (!step || !scrim || !card) return;
    const vp = viewport();
    const { rects } = measureTargets(step);
    if (step.targets.length > 0 && rects.size === 0) {
      // The step's targets vanished mid-step (a re-render swapped the view).
      // Same contract as at render time: skip visibly, never a holeless scrim.
      skipMissing(step);
      return;
    }
    const res = clipPathFor(step.targets, rects, vp);
    if (res.clip !== lastClip) {
      lastClip = res.clip;
      scrim.style.clipPath = res.clip;
    }
    const cw = card.offsetWidth;
    const ch = card.offsetHeight;
    const placed = placeCard(res.boxes, vp, { w: cw, h: ch }, step.placement ?? 'auto');
    const left = Math.round(placed.left);
    const top = Math.round(placed.top);
    if (left !== lastLeft || top !== lastTop) {
      lastLeft = left;
      lastTop = top;
      card.style.left = `${left}px`;
      card.style.top = `${top}px`;
    }
  };

  const scheduleMeasure = (): void => {
    if (moScheduled || state.ended) return;
    moScheduled = true;
    requestAnimationFrame(() => {
      moScheduled = false;
      measure();
    });
  };

  const skipMissing = (step: FlowStep): void => {
    log(`[onboarding] ${flow.id}: ${stepLabel(state.index)} target not found — step skipped`);
    state = seqReduce(state, 'missing', stepCount);
    if (state.ended) finish(state.reason ?? 'completed');
    else showStep();
  };

  /**
   * Render the current step. When the step's targets are absent, probe once
   * more after a double rAF (targets may mount/animate in a beat after the
   * overlay — the reference's measured lesson), then auto-skip.
   */
  const showStep = (retried = false): void => {
    if (state.ended) return;
    const step = steps[state.index];
    if (!step) {
      finish('completed');
      return;
    }
    const token = ++renderToken;
    const { rects, els } = measureTargets(step);
    if (step.targets.length > 0 && rects.size === 0) {
      if (!retried) {
        requestAnimationFrame(() => requestAnimationFrame(() => {
          if (token === renderToken && !state.ended) showStep(true);
        }));
        return;
      }
      skipMissing(step);
      return;
    }

    // Bring an off-viewport target on screen BEFORE clipping to it (instant:
    // smooth scroll would race the measurement and fights reduced-motion).
    const vp = viewport();
    for (const el of els) {
      const r = el.getBoundingClientRect();
      if (r.bottom < 0 || r.top > vp.h || r.right < 0 || r.left > vp.w) {
        el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    }

    ensureNodes();
    state = seqReduce(state, 'shown', stepCount);
    hooks.onStepShow?.(flow.id, state.index);

    card?.remove();
    card = buildCard(step, state.index);
    lastLeft = NaN;
    lastTop = NaN;
    if (!enteredOnce) {
      card.classList.add('ob-entrance');
      enteredOnce = true;
    }
    card.style.visibility = 'hidden';
    layer?.appendChild(card);
    measure();
    card.style.visibility = '';
    const primary = card.querySelector<HTMLButtonElement>('.ob-btn.ob-primary');
    primary?.focus({ preventScroll: true });

    // Re-observe the current targets.
    ro?.disconnect();
    if (typeof ResizeObserver !== 'undefined') {
      ro = ro ?? new ResizeObserver(() => scheduleMeasure());
      const fresh = measureTargets(step).els;
      for (const el of fresh) ro.observe(el);
    }

    // Late mounts / entrance animations settle a frame or two after render.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (token === renderToken && !state.ended) measure();
    }));
  };

  const advance = (): void => {
    state = seqReduce(state, 'next', stepCount);
    if (state.ended) finish(state.reason ?? 'completed');
    else showStep();
  };

  const goBack = (): void => {
    const before = state.index;
    state = seqReduce(state, 'back', stepCount);
    if (state.index !== before) showStep();
  };

  const onDocPointerDown = (e: Event): void => {
    if (state.ended) return;
    const step = steps[state.index];
    if (!step || step.targets.length === 0) return;
    const t = e.target;
    if (!(t instanceof Element)) return;
    if (t.closest(targetSelector(step))) {
      // Click-through: the real control fires natively AND the tour advances.
      advance();
    }
  };

  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      finish('dismissed');
    }
  };

  const onResize = (): void => measure();
  const onScroll = (): void => measure();

  const finish = (reason: FlowEndReason): void => {
    if (!state.ended) state = seqReduce(state, reason === 'skipped' ? 'skip' : 'dismiss', stepCount);
    // seqReduce is absorbing, so an already-ended state (walked off the end)
    // keeps its own reason; the parameter wins otherwise.
    const finalReason = state.reason ?? reason;
    renderToken++;
    document.removeEventListener('pointerdown', onDocPointerDown);
    document.removeEventListener('keydown', onKeyDown, true);
    window.removeEventListener('resize', onResize);
    document.removeEventListener('scroll', onScroll, true);
    ro?.disconnect();
    ro = null;
    mo?.disconnect();
    mo = null;
    card?.remove();
    card = null;
    layer?.remove();
    layer = null;
    scrim?.remove();
    scrim = null;
    ACTIVE = null;
    if (priorFocus && priorFocus.isConnected) {
      try { priorFocus.focus({ preventScroll: true }); } catch { /* focus is best-effort */ }
    }
    hooks.onFlowEnd?.(flow.id, finalReason, {
      shownSteps: state.shown.length,
      missingSteps: state.missing.length,
      totalSteps: stepCount,
    });
  };

  const handle: FlowHandle = {
    flowId: flow.id,
    next: () => { if (!state.ended) advance(); },
    back: () => { if (!state.ended) goBack(); },
    dismiss: (reason: FlowEndReason = 'dismissed') => { if (!state.ended) finish(reason); },
    isActive: () => !state.ended,
  };
  ACTIVE = { flowId: flow.id, handle };

  document.addEventListener('pointerdown', onDocPointerDown);
  document.addEventListener('keydown', onKeyDown, true);
  window.addEventListener('resize', onResize);
  document.addEventListener('scroll', onScroll, true);
  if (typeof MutationObserver !== 'undefined') {
    mo = new MutationObserver((muts) => {
      // The overlay's own writes fire attribute mutations; measuring on our
      // own echoes would be a self-wake loop (write-on-change already stops
      // the WRITES; this stops the wakeups). Any mutation OUTSIDE the
      // overlay schedules a re-measure.
      for (const m of muts) {
        const t = m.target;
        if (t instanceof Element && (scrim === t || layer?.contains(t))) continue;
        scheduleMeasure();
        return;
      }
    });
    mo.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });
  }

  showStep();
  // The first step may have ended the flow synchronously (every target
  // missing on a single-step flow, etc.) — report honestly.
  return state.ended ? null : handle;
}
